import React, {
	forwardRef,
	lazy,
	Suspense,
	useContext,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import appConfig from "config.json";

import { Flyout, SectionTitle } from "@wdpr/groot-components";
import { Context } from "Context";
import useKeyboardNav from "hooks/useKeyboardNav";
import LinkGroup from "./LinkGroup";
import LinkGroupTxp from "./section-txp/LinkGroupTxp";
import { toggleBodyClass } from "utils";
import { useIconButtonVariant } from "hooks/txp/useIconButtonVariant";
import { WdprScrollbar } from "components/vista";
import { SectionTitleTxp } from "./section-txp/SectionTitleTxp";
import { txpMotionDelayMs, isProfileSection } from "config-txp";
import { useCallback } from "react";

const ScrollWrapper = ({ id, children, txp, isMobileView }) => {
	if (txp && !isMobileView) {
		return (
			<WdprScrollbar minThumbSize="30" size="small" orientation="vertical">
				{children}
			</WdprScrollbar>
		);
	}
	return <>{children}</>;
};

const defaultNavHeight = 64;
const mainClass = "syndicated-section";
const preventScrollTxpClass = "navigation-prevent-scroll-txp";
const navigationFlyoutSectionsTopVariable = "--navigation-flyout-sections-top";
const flyoutQuery = `.syndicated-flyout.syndicated-flyout`;

const { sectionsConfiguration, brands } = appConfig;

const FlyoutPrimary = lazy(() =>
	import("@wdpr/groot-components").then((m) => ({ default: m.Flyout.Primary }))
);
const FlyoutSecondary = lazy(() =>
	import("@wdpr/groot-components").then((m) => ({ default: m.Flyout.Generic }))
);

const getClasses = ({
	mainClass,
	id,
	brand,
	isMobileView,
	collapse,
	showFlyout,
	isMenuActive,
	onFocus,
	store,
	isDecorative,
	showInMobile,
	onHover,
}) => {
	let classes = mainClass;
	if (id) classes += ` ${mainClass}--${id}`;
	if (brand) classes += ` ${mainClass}--${brand}`;
	if (isMobileView && collapse) classes += ` ${mainClass}--open`;
	if (!showFlyout) classes += ` ${mainClass}--no-flyout`;
	if (isMenuActive && onFocus) classes += ` ${mainClass}--onfocus`;
	if (store) classes += ` ${mainClass}--store`;
	if (isDecorative) classes += ` ${mainClass}--decorative`;
	if (showInMobile && isDecorative) classes += ` ${mainClass}--show-in-mobile`;
	if (onHover) classes += ` ${mainClass}--onhover`;

	return classes;
};

function renderFlyoutGroup({
	group,
	id,
	mainClass,
	isMobileView,
	isDecorative,
	showInMobile,
	reduceLayout,
	handleFullClose,
	brand,
	background,
	promosContainer,
	groupType,
	isTxp = false,
	hoveredSection,
	isHeaderHovered,
}) {
	if (
		!group ||
		(!group.descriptions?.length &&
			!group.modules?.length &&
			!group.column1 &&
			!group.column2)
	)
		return null;
	const columns = group?.columns || 1;
	return (
		<FlyoutSecondary
			name={id}
			background={background}
			brand={brand}
			promosContainer={promosContainer}
			isDecorative={isDecorative}
			showInMobile={showInMobile}
			reduceLayout={reduceLayout}
			type={groupType}
			columns={columns}
			txp={isTxp}
		>
			<div
				className={`${mainClass}__content ${
					isTxp ? `${mainClass}__content__txp` : ""
				} ${mainClass}__content--${id}`}
			>
				{isTxp ? (
					<LinkGroupTxp
						{...group}
						id={id}
						mainClass={`${mainClass}__content--${id}__${groupType}`}
						isMobileView={isMobileView}
						isDecorative={isDecorative}
						groupType={groupType}
						showInMobile={showInMobile}
						reduceLayout={reduceLayout}
						handleFullClose={handleFullClose}
						txp={isTxp}
						columns={columns}
						hoveredSection={hoveredSection}
						isHeaderHovered={isHeaderHovered}
					/>
				) : (
					<LinkGroup
						{...group}
						id={id}
						mainClass={`${mainClass}__content--${id}__${groupType}`}
						isMobileView={isMobileView}
						isDecorative={isDecorative}
						showInMobile={showInMobile}
						reduceLayout={reduceLayout}
						handleFullClose={handleFullClose}
						txp={isTxp}
						columns={columns}
						secondary
					/>
				)}
			</div>
		</FlyoutSecondary>
	);
}

const SectionTxp = forwardRef(
	(
		{
			id,
			title,
			showTitleInMobile,
			icon,
			link,
			primary,
			secondary,
			tertiary,
			showFlyout = true,
			collapse,
			accessibilityText,
			setCollapsedSection,
			isMenuActive,
			setIsMenuActive,
			store,
			brand,
			flyoutPosition,
			flyoutColumn,
			isLastSection,
			decorativeSection,
			showInMobile,
			reduceLayout,
			showSecondary,
			image,
			handleFullClose,
			card,
			compactDelays,
			txp: isTxp = false,
			backdropRef,
			headerRef,
			setHoveredSection,
			hoveredSection,
			sectionsHovered,
			isHeaderHovered,
			isScrolling,
			setSearchBarToggle,
			searchBarToggle,
		},
		ref
	) => {
		const { isMobileView, cdnUrl, cdnAssetsUrl, config } = useContext(Context);
		const sectionRef = useRef();
		const hovered = useRef(false);
		const preventMouseEvents = useRef(false);
		const isMouseRef = useRef(false);
		const variant = useIconButtonVariant({
			toggle: isHeaderHovered,
			isScrolling,
			brand,
		});
		useImperativeHandle(ref, () => sectionRef.current);
		const [onHover, setOnHover] = useState(false);
		const { onFocus, handleKeyDown, handleOnFocus, handleOnBlur } =
			useKeyboardNav(
				sectionRef,
				isMenuActive,
				setIsMenuActive,
				isLastSection,
				isTxp
			);

		const isDecorative = decorativeSection === "true";
		const isColumn = flyoutColumn === "true";

		const currentBrandConfig = brands[brand] || {};
		const duplicateSectionLinkOnMobile =
			currentBrandConfig.duplicateSectionLinkOnMobile || false;
		const handleCollapse = (e) => {
			if (isMobileView) {
				e?.preventDefault();
				setCollapsedSection((currentCollapsedSection) =>
					currentCollapsedSection === id ? "" : id
				);
			}
		};
		const handleMouseEnter = useCallback(() => {
			if (preventMouseEvents.current) {
				return;
			}
			if (searchBarToggle && isProfileSection(id)) {
				return;
			}
			setOnHover(true);
			setHoveredSection?.(id);
			if (config.isTxp && isMobileView) {
				// Don't close search on hover - search stays open
				if (!searchBarToggle) {
					toggleBodyClass(true, preventScrollTxpClass);
				}
			}
		}, [config.isTxp, id, isMobileView, searchBarToggle, setHoveredSection]);

		const handleMouseLeave = useCallback(() => {
			if (preventMouseEvents.current) {
				preventMouseEvents.current = false;
				return;
			}
			// If the mouse is still within the header, keep the section open.
			// The sections container's onMouseLeave or isHeaderHovered going false
			// will handle the true "left navigation" case.
			if (isHeaderHovered) {
				return;
			}
			preventMouseEvents.current = false;
			setOnHover(false);
			setIsMenuActive?.(false);
			// When searchBarToggle is active, don't clear hoveredSection on leave.
			// The sections container's onMouseLeave handles the true "left all sections" case.
			// This prevents the flash where hoveredSection goes null between sections.
			if (!searchBarToggle) {
				setHoveredSection?.(null);
			}
			if (config.isTxp && isMobileView) {
				if (!searchBarToggle) {
					toggleBodyClass(false, preventScrollTxpClass);
				}
			}
		}, [
			config.isTxp,
			isMobileView,
			isHeaderHovered,
			searchBarToggle,
			setHoveredSection,
			setIsMenuActive,
		]);

		const handleTxpSectionButtonPressed = useCallback(() => {
			if (isMouseRef.current) {
				isMouseRef.current = false;
				return;
			}
			if (searchBarToggle && isProfileSection(id)) {
				setSearchBarToggle?.(false);
				return;
			}
			if (hoveredSection === id) {
				preventMouseEvents.current = true;
				setHoveredSection?.(null);
				setIsMenuActive?.(false);
				if (isMobileView) {
					toggleBodyClass(false, preventScrollTxpClass);
				}
				setTimeout(() => {
					preventMouseEvents.current = false;
				}, 300);
			} else {
				preventMouseEvents.current = true;
				setHoveredSection?.(id);
				if (isMobileView) {
					toggleBodyClass(true, preventScrollTxpClass);
				}
			}
		}, [
			hoveredSection,
			id,
			isMobileView,
			searchBarToggle,
			setHoveredSection,
			setSearchBarToggle,
			setIsMenuActive,
		]);

		const updateSectionsTop = () => {
			const headerRect = headerRef?.current?.getBoundingClientRect();
			// Stop updating once header is out of viewport
			if (!headerRect || headerRect.bottom <= 0) return;

			const backdropRect = backdropRef?.current?.getBoundingClientRect();
			if (backdropRect) {
				document?.documentElement.style.setProperty(
					navigationFlyoutSectionsTopVariable,
					`${backdropRect.y + defaultNavHeight}px`
				);
			}
		};

		useEffect(() => {
			try {
				const element = document?.querySelector(
					`${flyoutQuery}--${CSS.escape(id)}`
				);
				if (backdropRef?.current && hoveredSection && hoveredSection === id) {
					backdropRef.current.style.height = `${64 + element?.offsetHeight}px`;
				}
			} catch {
				// Fallback if selector is invalid — reset to default height
				if (backdropRef?.current) {
					backdropRef.current.style.height = `${defaultNavHeight}px`;
				}
			}
			setOnHover(hoveredSection === id);
			hovered.current = hoveredSection === id;
			if (!hoveredSection && !hovered.current) {
				preventMouseEvents.current = false;
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [hoveredSection, sectionsHovered]);

		useEffect(() => {
			let rafId = null;
			const header = headerRef?.current;

			// Throttle scroll updates to one per animation frame (~60fps)
			const handleScroll = () => {
				if (rafId) return;
				rafId = requestAnimationFrame(() => {
					updateSectionsTop();
					rafId = null;
				});
			};

			updateSectionsTop();
			window.addEventListener("scroll", handleScroll);
			header?.addEventListener("mouseenter", updateSectionsTop);
			return () => {
				window.removeEventListener("scroll", handleScroll);
				header?.removeEventListener("mouseenter", updateSectionsTop);
				if (rafId) cancelAnimationFrame(rafId);
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		if (!title) return null;

		return (
			<div
				className={getClasses({
					mainClass,
					id,
					brand,
					isMobileView,
					collapse,
					showFlyout,
					isMenuActive,
					onFocus,
					store,
					isDecorative,
					showInMobile,
					onHover,
				})}
				tabIndex={isMobileView ? null : -1}
				ref={sectionRef}
				onKeyDown={handleKeyDown}
				{...(!isTxp ? { onFocus: handleOnFocus, onBlur: handleOnBlur } : {})}
				onMouseDown={(e) => {
					// Real mouse click has detail >= 1; JAWS simulated events have detail: 0
					if (e.detail > 0) {
						isMouseRef.current = true;
						setTimeout(() => {
							isMouseRef.current = false;
						}, 200);
					}
				}}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				role="group"
			>
				{isTxp ? (
					<SectionTitleTxp
						variant={variant}
						title={title}
						id={id}
						hoveredSection={hoveredSection}
						handleTxpSectionButtonPressed={handleTxpSectionButtonPressed}
						link={link}
						defaultAvatar={primary?.image?.url || ""}
					/>
				) : (
					<SectionTitle
						collapse={isMobileView ? collapse : onHover || isMenuActive}
						link={{
							...link,
							handleClick: showFlyout ? handleCollapse : null,
						}}
						icon={icon}
						title={title}
						name={id}
						isMobileView={isMobileView}
						accessibilityText={accessibilityText}
						brand={brand}
						showFlyout={showFlyout}
						cdnUrl={cdnUrl}
						cdnAssetsUrl={cdnAssetsUrl}
						card={card}
						txp={isTxp}
					/>
				)}
				{showFlyout && (
					<Suspense fallback={null}>
						<Flyout
							collapse={collapse && onHover}
							left={
								flyoutPosition
									? flyoutPosition === "left"
									: sectionsConfiguration.flyoutPosition.left.includes(id)
							}
							column={sectionsConfiguration.column.includes(id) || isColumn}
							name={id}
							store={store}
							txp={isTxp}
							id={id ? `syndicated-flyout-${id}` : undefined}
						>
							<ScrollWrapper id={id} txp={isTxp} isMobileView={isMobileView}>
								<div className="syndicated-flyout__columns">
									{primary && (
										<Suspense fallback={null}>
											<FlyoutPrimary
												name={id}
												isMobileView={isMobileView}
												image={image}
												txp={isTxp}
											>
												<div
													className={`${mainClass}__content ${mainClass}__content--${id}`}
												>
													{isMobileView &&
														showSecondary &&
														(config.isTxp && isTxp ? (
															<LinkGroupTxp
																{...secondary}
																showTitleInMobile={showTitleInMobile === "true"}
																duplicateSectionLinkOnMobile={
																	duplicateSectionLinkOnMobile
																}
																link={link}
																id={id}
																mainClass={`${mainClass}__content--${id}__primary`}
																isMobileView={isMobileView}
																handleFullClose={handleFullClose}
																animationInitialDelay={0}
																compactDelays={compactDelays}
																hoveredSection={hoveredSection}
																isHeaderHovered={isHeaderHovered}
															/>
														) : (
															<LinkGroup
																{...secondary}
																duplicateSectionLinkOnMobile={
																	duplicateSectionLinkOnMobile
																}
																link={link}
																id={id}
																mainClass={`${mainClass}__content--${id}__primary`}
																isMobileView={isMobileView}
																handleFullClose={handleFullClose}
															/>
														))}
													{config.isTxp && isTxp ? (
														<LinkGroupTxp
															{...primary}
															duplicateSectionLinkOnMobile={
																duplicateSectionLinkOnMobile
															}
															link={link}
															id={id}
															mainClass={`${mainClass}__content--${id}__primary`}
															isMobileView={isMobileView}
															handleFullClose={handleFullClose}
															animationInitialDelay={
																txpMotionDelayMs.betweenAnimations
															}
															compactDelays={compactDelays}
															hoveredSection={hoveredSection}
															isHeaderHovered={isHeaderHovered}
														/>
													) : (
														<LinkGroup
															{...primary}
															duplicateSectionLinkOnMobile={
																duplicateSectionLinkOnMobile
															}
															link={link}
															id={id}
															mainClass={`${mainClass}__content--${id}__primary`}
															isMobileView={isMobileView}
															handleFullClose={handleFullClose}
														/>
													)}
												</div>
											</FlyoutPrimary>
										</Suspense>
									)}
									{renderFlyoutGroup({
										group: secondary,
										id,
										mainClass,
										isMobileView,
										isDecorative,
										showInMobile,
										reduceLayout,
										handleFullClose,
										brand,
										background:
											appConfig.brands[brand]?.secondaryFlyoutBackground ||
											"blue",
										promosContainer: secondary?.promosContainer,
										groupType: "secondary",
										isTxp,
										hoveredSection,
										isHeaderHovered,
									})}
									{renderFlyoutGroup({
										group: tertiary,
										id,
										mainClass,
										isMobileView,
										isDecorative,
										showInMobile,
										reduceLayout,
										handleFullClose,
										brand,
										background:
											appConfig.brands[brand]?.tertiaryFlyoutBackground ||
											"blue",
										promosContainer: tertiary?.promosContainer,
										groupType: "tertiary",
										isTxp,
										hoveredSection,
										isHeaderHovered,
									})}
								</div>
							</ScrollWrapper>
						</Flyout>
					</Suspense>
				)}
			</div>
		);
	}
);

export default SectionTxp;
